<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import {
    getCountryList,
    markerStyles,
    type Country,
    type MapEngine,
  } from '@atlasguess/engine';
  import { createAudio, type AudioPlayer } from '@atlasguess/audio';
  import {
    applyEngineConfig,
    CONTINENT_CAMERAS,
    filterCountries,
    Picker,
    resolveFilterSlug,
    zoomForCountry,
    type CountryPracticeConfig,
  } from '@atlasguess/games';
  import Prompt from '../framework/Prompt.svelte';
  import NextButton from '../framework/NextButton.svelte';
  import FilterPicker from '../framework/FilterPicker.svelte';
  import { soundEnabled } from '../../lib/sound';
  import { recordRun } from '../../lib/runs';
  import { mobileBumpedMarker } from '../../lib/mobile';

  interface Props {
    config: CountryPracticeConfig;
    engine: MapEngine;
    /** ModeFrame's reveal-veil callback. Called once polygons are in. */
    markReady: () => void;
    /**
     * Optional URL filter slug (`/play/.../practice/europe`). Layered on
     * top of `config.countryFilter` so a continent slug narrows the pool
     * to just that continent within the mode's base set; a country slug
     * narrows it to a single country. Resolved against `getCountryList()`
     * inside `initialize()` because the lookup needs the dataset.
     */
    filter?: string;
  }

  let { config, engine, markReady, filter }: Props = $props();

  type Phase = 'loading' | 'awaiting-guess' | 'showing-result';
  type Outcome = 'correct' | 'wrong';

  let phase = $state<Phase>('loading');
  let target = $state<Country | null>(null);
  let lastOutcome = $state<Outcome | null>(null);
  let lastClicked = $state<Country | null>(null);

  /**
   * Consecutive correct guesses for the current session. Increments on
   * a correct round, resets to 0 on a wrong round. Page reload starts
   * fresh — server-side persistence of the BEST streak lands in a
   * later phase. Surfaced in the result card below the primary line
   * so the player gets immediate visible reward for keeping a run
   * going.
   */
  let currentStreak = $state(0);
  let picker: Picker<Country> | null = null;
  let audio: AudioPlayer | undefined;
  let restoreEngineConfig: (() => void) | null = null;
  let allCountries: Country[] = [];
  /**
   * ISOs in the active pool — gates clicks AND drives which countries
   * get small-country dots. Out-of-pool countries are greyed via the
   * engine's `inactive` fill state. World practice currently has an
   * empty filter so every country is in-pool (no greying); region-
   * filtered practice modes (future) get the same grey-out behavior
   * the quizzes do for free.
   */
  let poolIsos = $state<Set<string>>(new Set());

  /** Marker layer for the auto-placed small-country pins. Cleared on unmount. */
  const SMALL_COUNTRY_LAYER = 'country_pins';

  /**
   * Re-tint a small-country dot to match the polygon highlight states
   * (gray default → green correct → orange incorrect). Re-adding a marker
   * with the same id replaces the previous one in place. No-op for
   * countries that don't have a dot — large countries highlight via the
   * polygon fill instead, which is handled separately.
   */
  function paintCountryDot(country: Country, variant: 'neutral' | 'correct' | 'incorrect'): void {
    if (!country.needs_dot) return;
    const style =
      variant === 'correct'
        ? markerStyles.countryPinCorrect
        : variant === 'incorrect'
          ? markerStyles.countryPinIncorrect
          : markerStyles.countryPin;
    engine.markers.add({
      id: `country-pin-${country.iso_a2}`,
      position: { lat: country.centroid[1], lon: country.centroid[0] },
      style: mobileBumpedMarker(style),
      layer: SMALL_COUNTRY_LAYER,
      data: country,
    });
  }

  /** Target country is shown in the prompt, so wrong-answer feedback only
   *  needs to echo what the player actually clicked. */
  const resultLines = $derived.by((): { primary: string; secondary?: string } | null => {
    if (lastOutcome === null || !target) return null;
    if (lastOutcome === 'correct') return { primary: 'Correct!', secondary: target.name };
    return {
      primary: lastClicked ? `You clicked: ${lastClicked.name}` : 'Not quite',
    };
  });

  onMount(() => {
    audio = createAudio({ enabled: $soundEnabled });
    engine.setOnCountryClick(handleClick);
    void initialize();
  });

  // Live-track the user's sound setting — flipping it in /account
  // silences future dings without needing to remount the mode.
  $effect(() => {
    audio?.setEnabled($soundEnabled);
  });

  async function initialize(): Promise<void> {
    // Polygons + metadata. loadBorders builds the hit-tester + fills mesh
    // (so setFillState works); getCountryList gives us the typed list to
    // filter through and pick prompts from.
    await engine.loadBorders();
    // Layer config goes AFTER loadBorders — the engine resets fills to
    // hidden post-build (defensive default for the demo) and would
    // otherwise clobber our `fills.visible: true` setting.
    restoreEngineConfig = applyEngineConfig(engine, config.layers);
    allCountries = await getCountryList();
    // Compose the URL filter slug (continent or country) on top of the
    // mode's base countryFilter. `additionalIsos` is intentionally
    // dropped on URL-narrowed pools — the slug is the player's explicit
    // narrowing; force-includes from the base config (e.g. Western Sahara
    // on Africa) shouldn't leak into a "just Spain" sub-filter.
    const resolved = resolveFilterSlug(filter, allCountries);
    const filterCriteria = resolved.continentName
      ? { ...config.countryFilter, continents: [resolved.continentName], additionalIsos: undefined }
      : resolved.countryIso
        ? { ...config.countryFilter, isos: [resolved.countryIso], additionalIsos: undefined }
        : config.countryFilter;
    const pool = filterCountries(allCountries, filterCriteria);
    if (pool.length === 0) {
      console.warn('[atlas] country filter returned no countries; mode cannot start');
      return;
    }
    poolIsos = new Set(pool.map((c) => c.iso_a2));

    // Animate the camera to the filtered region so the player opens
    // looking at the relevant slice of the globe instead of the default
    // world view. Same easing pattern as the region quizzes — the loading
    // veil fades while the pan finishes, so the user sees the tail end
    // of the motion as gameplay UI appears. No-op when the filter is
    // empty (engine starts at world view, which is what we want).
    if (filter && CONTINENT_CAMERAS[filter]) {
      engine.setCamera({ ...CONTINENT_CAMERAS[filter], animate: true });
    } else if (resolved.countryIso) {
      const c = allCountries.find((x) => x.iso_a2 === resolved.countryIso);
      if (c) {
        engine.setCamera({
          lat: c.centroid[1],
          lon: c.centroid[0],
          zoom: zoomForCountry(c),
          animate: true,
        });
      }
    }

    // Grey out non-pool countries via the engine's 'inactive' fill state.
    // World practice has an empty filter → every country is in-pool →
    // this loop is a no-op and the visual is unchanged.
    for (const c of allCountries) {
      if (!poolIsos.has(c.iso_a2)) {
        engine.layers.outline.setFillState(c.iso_a2, 'inactive');
      }
    }
    // Place dots ONLY for in-pool needs_dot countries — out-of-pool
    // dots would float over greyed-out polygons, confusing the player.
    for (const c of pool) {
      if (c.needs_dot) paintCountryDot(c, 'neutral');
    }

    picker = new Picker(pool, (c) => c.iso_a2);
    markReady();
    nextRound();
  }

  function nextRound(): void {
    if (!picker) return;
    // clearFillStates wipes EVERY country's state, including the
    // 'inactive' marks on out-of-pool countries — re-apply them so
    // the grey-out persists through the run.
    engine.layers.outline.clearFillStates();
    for (const c of allCountries) {
      if (!poolIsos.has(c.iso_a2)) {
        engine.layers.outline.setFillState(c.iso_a2, 'inactive');
      }
    }
    // Reset last round's tinted dots back to neutral so the answer
    // reveal doesn't leak into the next prompt.
    if (target) paintCountryDot(target, 'neutral');
    if (lastClicked && lastClicked.iso_a2 !== target?.iso_a2) {
      paintCountryDot(lastClicked, 'neutral');
    }
    audio?.cancel();
    lastOutcome = null;
    lastClicked = null;
    target = picker.next();
    phase = 'awaiting-guess';
  }

  function handleClick(country: Country | null): void {
    if (phase !== 'awaiting-guess' || !target || !audio) return;
    // Ocean / unassigned clicks don't end the round — user can try again.
    if (!country) return;
    // Out-of-pool countries are visually greyed AND silently
    // unclickable — same UX contract as the quiz mode.
    if (!poolIsos.has(country.iso_a2)) return;

    lastClicked = country;
    const correct = country.iso_a2 === target.iso_a2;
    if (correct) {
      lastOutcome = 'correct';
      currentStreak += 1;
      engine.layers.outline.setFillState(target.iso_a2, 'correct');
      paintCountryDot(target, 'correct');
      audio.ding({ frequencies: config.audio.correctFrequencies });
    } else {
      lastOutcome = 'wrong';
      currentStreak = 0;
      engine.layers.outline.setFillState(country.iso_a2, 'incorrect');
      engine.layers.outline.setFillState(target.iso_a2, 'correct');
      paintCountryDot(country, 'incorrect');
      paintCountryDot(target, 'correct');
      audio.ding({ frequencies: config.audio.incorrectFrequencies });
    }
    phase = 'showing-result';

    // One row per round, score = 1 (correct) | 0 (wrong). Unique run_key
    // per click means each guess persists as its own immutable record.
    // filter_slug + current_streak ride along so the server can
    // maintain a per-(mode + filter) best-streak record. current_streak
    // here is the post-outcome value (already incremented or reset
    // above), so wrong rounds send 0 — server's max-only update makes
    // that a no-op.
    void recordRun({
      mode_id: config.id,
      format: 'practice',
      run_key: `${config.id}-${Date.now()}`,
      filter_slug: filter ?? '',
      current_streak: currentStreak,
      rounds: [{
        target_id: target.iso_a2,
        score: correct ? 1 : 0,
        score_unit: 'count',
      }],
    });
  }

  onDestroy(() => {
    engine.setOnCountryClick(undefined);
    engine.layers.outline.clearFillStates();
    engine.markers.clear(SMALL_COUNTRY_LAYER);
    audio?.cancel();
    audio?.dispose();
    restoreEngineConfig?.();
  });
</script>

{#if config.userMayChangeFilter && phase !== 'loading'}
  <!-- Country picker: continents only (no per-country option). Per-country
       narrowing would degenerate to a one-target prompt loop, which isn't
       interesting for the country-tap interaction. The dropdown can grow
       to include curated country sets later (G20, EU, etc.). -->
  <FilterPicker
    scopeSlug={`${config.scope.entity}-${config.scope.region}`}
    format={config.format}
    activeSlug={filter}
    countries={[]}
    region={config.scope.region}
  />
{/if}

{#if phase !== 'loading' && target}
  <Prompt
    primary={target.name}
    flagUrl={`/${target.flag_file}`}
    active={phase === 'awaiting-guess'}
  />
{/if}

{#if phase === 'showing-result' && resultLines}
  <div class="round-result round-result--card">
    <div class="round-result__panel">
      <div
        class="round-result__primary"
        class:round-result__primary--wrong={lastOutcome === 'wrong'}
      >
        {resultLines.primary}
      </div>
      {#if resultLines.secondary}
        <div class="round-result__secondary">{resultLines.secondary}</div>
      {/if}
      {#if lastOutcome === 'correct' && currentStreak > 1}
        <!-- Streak chip — only surfaces from streak 2 upward; a single
             correct doesn't deserve the celebration treatment. -->
        <div class="round-result__streak" aria-label="Correct-streak">
          <span class="round-result__streak-flame" aria-hidden="true">🔥</span>
          <span class="round-result__streak-num">{currentStreak}</span>
          <span class="round-result__streak-word">streak</span>
        </div>
      {/if}
    </div>
    <NextButton onclick={nextRound} spaceHotkey />
  </div>
{/if}
